/** Root application component.
 *
 * Two modes based on the current page:
 *   1. Normal pages: search bar + instant dropdown. Submit navigates to /catalogsearch/result/.
 *   2. catalogsearch page: search bar + full ResultsPage rendered in normal document flow.
 */

import { h } from 'preact';
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { SearchBar } from './SearchBar';
import { ResultsPage } from './ResultsPage';
import type { VendorTab, VendorCounts } from './ResultsPage';
import { addToSearchHistory } from './Autocomplete';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { getConfig } from '../config';
import { search as apiSearch } from '../api';
import type { SearchParams } from '../api';


function parseUrlParams(): { q: string; page: number; category: string; vendor: VendorTab } {
  const params = new URLSearchParams(window.location.search);
  const site = params.get('site');
  const v = params.get('vendor');
  return {
    q: params.get('q') || '',
    page: parseInt(params.get('page') || '1', 10) || 1,
    category: params.get('category') || '',
    vendor: (site === 'le' || v === '!terra' ? 'le' : 'terra') as VendorTab,
  };
}

function pushUrl(q: string, page: number, vendor: VendorTab) {
  const url = new URL(window.location.href);
  url.searchParams.set('q', q);
  if (page > 1) url.searchParams.set('page', String(page));
  else url.searchParams.delete('page');
  if (vendor === 'le') url.searchParams.set('site', 'le');
  else url.searchParams.delete('site');
  url.searchParams.delete('vendor');
  window.history.pushState({}, '', url.toString());
}

/** Are we on the Magento catalogsearch results page? */
function isResultsPage(): boolean {
  const cl = document.body.classList;
  return cl.contains('catalogsearch-result-index') || cl.contains('search-index-index');
}

function parsePriceFilter(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

interface SearchAppProps {
  /** When true, render the full results page. When false/undefined, render search bar only. */
  resultsMode?: boolean;
}

const EMPTY_VENDOR_COUNTS: VendorCounts = { terra: null, le: null };

export function SearchApp({ resultsMode }: SearchAppProps = {}) {
  const { q: initialQuery, page: initialPage, category: initialCategory, vendor: initialVendor } = parseUrlParams();
  const onResultsPage = resultsMode && isResultsPage();

  const [query, setQuery] = useState(initialQuery);
  const [page, setPage] = useState(initialPage);
  const [sort, setSort] = useState('relevance');
  const [vendorTab, setVendorTab] = useState<VendorTab>(initialVendor);
  const [vendorCounts, setVendorCounts] = useState<VendorCounts>(EMPTY_VENDOR_COUNTS);
  const skipFirstFilterSearchRef = useRef(true);
  const vendorCountsAbortRef = useRef<AbortController | null>(null);

  const searchHook = useSearch();
  const filterHook = useFilters();

  const { doSearch, results, isLoading, error } = searchHook;
  const { filters } = filterHook;
  const filterSignature = [
    Array.from(filters.categories).sort().join(','),
    Array.from(filters.certifications).sort().join(','),
    Array.from(filters.manufacturers).sort().join(','),
    filters.inStockOnly ? '1' : '0',
    filters.priceMin,
    filters.priceMax,
  ].join('|');

  const buildSearchParams = useCallback(
    (q: string, p: number, s: string, f: typeof filters, vt: VendorTab, perPageOverride?: number): SearchParams => {
      const config = getConfig();
      const params: SearchParams = {
        q: q.trim(),
        page: p,
        per_page: perPageOverride ?? config.perPage,
        sort: s,
        semantic: config.semantic,
        site: vt === 'terra' ? 'terra' : 'le',
        // Reading B (stakeholder request): collapse sibling
        // SKUs to one card per family so the customer sees one
        // representative card and can expand the FamilyStack carousel
        // inline to see every variant.  Replaces the prior approach of
        // showing every sibling as its own card (which produced ~14
        // near-duplicate cards for queries like "smart desiccator").
        dedup_families: true,
      };
      if (f.categories.size > 0) {
        params.category = Array.from(f.categories).join(',');
      }
      if (f.certifications.size > 0) {
        params.certifications = Array.from(f.certifications).join(',');
      }
      if (f.manufacturers.size > 0) {
        params.manufacturer = JSON.stringify(Array.from(f.manufacturers));
      }
      if (f.inStockOnly) params.in_stock = true;
      const priceMin = parsePriceFilter(f.priceMin);
      const priceMax = parsePriceFilter(f.priceMax);
      if (priceMin != null) params.price_min = priceMin;
      if (priceMax != null) params.price_max = priceMax;
      return params;
    },
    [],
  );

  // Execute search with current state
  const executeSearch = useCallback(
    (q: string, p: number, s: string, f: typeof filters, vt?: VendorTab) => {
      if (!q.trim()) return;
      const params = buildSearchParams(q, p, s, f, vt ?? 'terra');
      doSearch(params);
    },
    [buildSearchParams, doSearch],
  );

  // Handle search submit
  const handleSearch = useCallback(
    (q: string) => {
      if (!q.trim()) return;
      addToSearchHistory(q);

      if (onResultsPage) {
        setQuery(q);
        setPage(1);
        setVendorTab('terra');
        executeSearch(q, 1, sort, filters, 'terra');
        pushUrl(q, 1, 'terra');
      } else {
        const config = getConfig();
        window.location.href = `${config.resultsPageUrl}?q=${encodeURIComponent(q.trim())}`;
      }
    },
    [onResultsPage, sort, filters, executeSearch],
  );

  // Page change
  const handlePageChange = useCallback(
    (p: number) => {
      setPage(p);
      executeSearch(query, p, sort, filters, vendorTab);
      pushUrl(query, p, vendorTab);
      // Scroll to results, not page top
      const el = document.getElementById('terra-search-results-root');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [query, sort, filters, vendorTab, executeSearch],
  );

  // Sort change
  const handleSortChange = useCallback(
    (s: string) => {
      setSort(s);
      setPage(1);
      executeSearch(query, 1, s, filters, vendorTab);
      pushUrl(query, 1, vendorTab);
    },
    [query, filters, vendorTab, executeSearch],
  );

  // Vendor tab change
  const handleVendorTabChange = useCallback(
    (tab: VendorTab) => {
      setVendorTab(tab);
      setPage(1);
      executeSearch(query, 1, sort, filters, tab);
      pushUrl(query, 1, tab);
    },
    [query, sort, filters, executeSearch],
  );

  // Re-search when filters change — debounced so burst-toggles collapse into one call
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (skipFirstFilterSearchRef.current) {
      skipFirstFilterSearchRef.current = false;
      return;
    }
    if (!onResultsPage || !query) return;
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setPage(1);
      executeSearch(query, 1, sort, filters, vendorTab);
      pushUrl(query, 1, vendorTab);
    }, 300);
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, [filterSignature, onResultsPage, query, sort, vendorTab, executeSearch]);

  // Fetch exact totals for both vendor segments. The gateway scopes facets to the
  // active vendor filter, so segment counts must come from dedicated search totals.
  useEffect(() => {
    if (!onResultsPage || !query.trim()) {
      setVendorCounts(EMPTY_VENDOR_COUNTS);
      vendorCountsAbortRef.current?.abort();
      return;
    }

    vendorCountsAbortRef.current?.abort();
    const controller = new AbortController();
    vendorCountsAbortRef.current = controller;
    setVendorCounts(EMPTY_VENDOR_COUNTS);

    const terraParams = buildSearchParams(query, 1, sort, filters, 'terra', 1);
    const leParams = buildSearchParams(query, 1, sort, filters, 'le', 1);

    Promise.allSettled([
      apiSearch(terraParams, controller.signal),
      apiSearch(leParams, controller.signal),
    ]).then(([terraResult, leResult]) => {
      if (controller.signal.aborted) return;
      setVendorCounts({
        terra: terraResult.status === 'fulfilled' ? terraResult.value.total : null,
        le: leResult.status === 'fulfilled' ? leResult.value.total : null,
      });
    });

    return () => controller.abort();
  }, [buildSearchParams, filterSignature, filters, onResultsPage, query, sort]);

  // Document title — the Magento catalogsearch page ships with an empty
  // <title>, so the browser tab shows the raw URL. Keep the title in sync
  // with the active query (in-page re-searches update it too).
  useEffect(() => {
    if (!onResultsPage || !query.trim()) return;
    document.title = `Search results for "${query.trim()}"`;
  }, [onResultsPage, query]);

  // Initial search from URL
  useEffect(() => {
    if (onResultsPage && initialQuery) {
      if (initialCategory) {
        skipFirstFilterSearchRef.current = true;
        filterHook.toggleCategory(initialCategory);
      }
      executeSearch(initialQuery, initialPage, sort, filters, initialVendor);
    }
  }, []);

  // Browser back/forward
  useEffect(() => {
    if (!onResultsPage) return;
    const handler = () => {
      const { q, page: p, vendor: v } = parseUrlParams();
      if (q) {
        setQuery(q);
        setPage(p);
        setVendorTab(v);
        executeSearch(q, p, sort, filters, v);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [onResultsPage, sort, filters, executeSearch]);

  if (onResultsPage && query) {
    return (
      <div class="terra-search-widget terra-search-widget--results-page">
        <ResultsPage
          query={query}
          results={results}
          isLoading={isLoading}
          error={error}
          page={page}
          sort={sort}
          vendorTab={vendorTab}
          vendorCounts={vendorCounts}
          filterHook={filterHook}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onSearch={handleSearch}
          onVendorTabChange={handleVendorTabChange}
        />
      </div>
    );
  }

  return (
    <div class="terra-search-widget">
      <SearchBar
        onSearch={handleSearch}
        autocompleteHook={searchHook}
        initialValue=""
        compact={false}
      />
    </div>
  );
}
